import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import UkiesAssets from '../Admin/UkiesAssets';

export default function UkiesHighlightBar({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent }) {
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [textSourceCodeMode, setTextSourceCodeMode] = useState(false);
    
    const textEditorRef = useRef(null);
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;

    // Background color options
    const backgroundOptions = [
        { value: 'green-gradient', label: 'Groen (gradient)', style: 'radial-gradient(circle, rgba(56,98,79,1) 0%, rgba(59,79,70,1) 100%)', textClass: 'white' }
    ];

    // Get current values with defaults
    const backgroundColor = componentSettings.background_color || 'green-gradient';
    const textContent = componentSettings.text_content || '';
    const imageUrl = componentSettings.image_url || '';
    const buttonText = componentSettings.button_text || '';
    const buttonUrl = componentSettings.button_url || '';
    const buttonColor = componentSettings.button_color || 'default';

    // Parse HTML content to extract highlight bar configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            
            const mainDiv = doc.querySelector('.ukies-highlight-bar');
            if (!mainDiv) return null;
            
            const config = {};
            
            // Extract background color from style attribute
            const styleAttr = mainDiv.getAttribute('style') || '';
            if (styleAttr.includes('radial-gradient(circle, rgba(56,98,79,1) 0%, rgba(59,79,70,1) 100%)')) {
                config.background_color = 'green-gradient';
            }
            
            // Extract text content - it's directly in the component-text div
            const textComponent = doc.querySelector('.component-text');
            if (textComponent) {
                // Remove the class names and get the text content
                const textContent = textComponent.innerHTML.trim();
                // Remove any leading/trailing whitespace and newlines
                config.text_content = textContent.replace(/^\s+|\s+$/g, '');
            }
            
            // Extract image
            const imageComponent = doc.querySelector('.component-image img');
            if (imageComponent) {
                config.image_url = imageComponent.getAttribute('src') || '';
            }
            
            // Extract button
            const buttonComponent = doc.querySelector('.component-button a');
            if (buttonComponent) {
                config.button_url = buttonComponent.getAttribute('href') || '';
                
                const buttonElement = buttonComponent.querySelector('button');
                config.button_text = buttonElement ? buttonElement.textContent : '';
                config.button_color = buttonElement && buttonElement.classList.contains('highlight') ? 'highlight' : 'default';
            }
            
            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // Generate HTML content from current settings
    const generateHTMLContent = () => {
        return generateHTMLContentWithSettings(componentSettings);
    };

    // Generate HTML content with specific settings
    const generateHTMLContentWithSettings = (settings) => {
        const bgColor = settings.background_color || backgroundColor;
        const text = settings.text_content || textContent;
        const image = settings.image_url || imageUrl;
        const btnText = settings.button_text || buttonText;
        const btnUrl = settings.button_url || buttonUrl;
        const btnColor = settings.button_color || buttonColor;
        
        // Get background style and text class
        const bgOption = backgroundOptions.find(opt => opt.value === bgColor) || backgroundOptions[0];
        const backgroundStyle = bgOption.style;
        const textClass = bgOption.textClass;
        
        let html = `<div class='structure structure-fullwidth ukies-highlight-bar' style='background:${backgroundStyle};margin: var(--ukies-structure-padding-y) var(--ukies-structure-padding-x);max-width: unset;padding: 0;'>
    <div class='structure structure-1 gap-2'>`;

        // Add text component - text is directly in the div, not nested
        if (text) {
            html += `
        <div class='component component-text ${textClass} center px-10vw'>
            ${text}
        </div>`;
        }

        // Add image component (if image is provided)
        if (image) {
            html += `
        <div class='component component-image'>
            <img width='250' style='margin: 1rem auto 0px auto;' src='${image}' />
        </div>`;
        }

        // Add button component (if button text is provided)
        if (btnText && btnUrl) {
            const buttonClass = btnColor === 'highlight' ? ' class="highlight"' : '';
            html += `
        <div class='component component-button' style="justify-content: center;">
            <a href='${btnUrl}'>
                <button${buttonClass} type='button'>${btnText}</button>
            </a>
        </div>`;
        }

        html += `
    </div>
</div>`;

        return html;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                // Update the component settings with parsed values
                setComponentSettings(prev => ({
                    ...prev,
                    background_color: parsed.background_color,
                    text_content: parsed.text_content,
                    image_url: parsed.image_url,
                    button_text: parsed.button_text,
                    button_url: parsed.button_url,
                    button_color: parsed.button_color
                }));
            }
            setIsInitialized(true);
        }
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
    }, [selectedComponent?.id]);

    // Set initial content for text editor when data changes
    useEffect(() => {
        if (textEditorRef.current && textContent !== undefined) {
            // Only update editor content if it's not currently focused (being edited)
            const isFocused = document.activeElement === textEditorRef.current;
            
            if (!isFocused) {
                if (textSourceCodeMode) {
                    textEditorRef.current.value = textContent || '';
                } else {
                    textEditorRef.current.innerHTML = textContent || '<p>Voer hier de tekst in...</p>';
                }
            }
        }
    }, [textContent, textSourceCodeMode]);

    // Handle field changes and update HTML content
    const handleFieldChange = (fieldKey, value) => {
        onChange(fieldKey, value);
        
        // Update HTML content after a brief delay to ensure state is updated
        setTimeout(() => {
            const updatedSettings = { ...componentSettings, [fieldKey]: value };
            const newContent = generateHTMLContentWithSettings(updatedSettings);
            
            // Update local component settings
            setComponentSettings(prev => ({
                ...prev,
                [fieldKey]: value,
                content: newContent
            }));
            
            // Update content in parent component
            onChange('content', newContent);
        }, 0);
    };

    // Handle asset selection from UkiesAssets
    const handleAssetSelect = (asset) => {
        const imageUrl = asset.image_variations?.large || asset.image_variations?.medium || asset.url;
        handleFieldChange('image_url', imageUrl);
        setShowAssetSelector(false);
    };

    // Format text functions for rich text editor
    const formatText = (command, value = null) => {
        document.execCommand(command, false, value);
        textEditorRef.current.focus();
    };

    // Handle text change from contentEditable
    const handleTextChange = (e) => {
        const content = e.target.innerHTML;
        if (content === '<p>Voer hier de tekst in...</p>') {
            handleFieldChange('text_content', '');
        } else {
            handleFieldChange('text_content', content);
        }
    };

    // Handle text editor click to clear placeholder
    const handleTextEditorClick = () => {
        if (textEditorRef.current && textEditorRef.current.innerHTML === '<p>Voer hier de tekst in...</p>') {
            textEditorRef.current.innerHTML = '<p><br></p>';
            const range = document.createRange();
            const selection = window.getSelection();
            range.setStart(textEditorRef.current.firstChild, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    // Handle source code mode toggle
    const handleSourceCodeToggle = () => {
        if (textSourceCodeMode) {
            const sourceContent = textEditorRef.current.value;
            handleFieldChange('text_content', sourceContent);
        } else {
            const wysiwygContent = textEditorRef.current.innerHTML;
            handleFieldChange('text_content', wysiwygContent);
        }
        setTextSourceCodeMode(!textSourceCodeMode);
    };

    return (
        <div>
            {/* Background Color Selection */}
            <Form.Group className="mb-3">
                <Form.Label>Achtergrondkleur</Form.Label>
                <Form.Select
                    value={backgroundColor}
                    onChange={(e) => handleFieldChange('background_color', e.target.value)}
                >
                    {backgroundOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            {/* Text Content with WYSIWYG Editor */}
            <Form.Group className="mb-3">
                <Form.Label>Tekst inhoud</Form.Label>
                
                {/* Rich Text Editor Toolbar */}
                <div className="border rounded-top p-2 bg-light">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('bold')}
                        disabled={textSourceCodeMode}
                    >
                        <strong>B</strong>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('italic')}
                        disabled={textSourceCodeMode}
                    >
                        <em>I</em>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('underline')}
                        disabled={textSourceCodeMode}
                    >
                        <u>U</u>
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('insertUnorderedList')}
                        disabled={textSourceCodeMode}
                    >
                        • List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => formatText('insertOrderedList')}
                        disabled={textSourceCodeMode}
                    >
                        1. List
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) formatText('createLink', url);
                        }}
                        disabled={textSourceCodeMode}
                    >
                        🔗 Link
                    </Button>
                    <Button
                        variant={textSourceCodeMode ? "primary" : "outline-secondary"}
                        size="sm"
                        className="ms-auto"
                        onClick={handleSourceCodeToggle}
                    >
                        {textSourceCodeMode ? "WYSIWYG" : "</>"}
                    </Button>
                </div>
                
                {/* Editor Area */}
                {textSourceCodeMode ? (
                    <Form.Control
                        ref={textEditorRef}
                        as="textarea"
                        rows={6}
                        value={textContent}
                        onChange={(e) => handleFieldChange('text_content', e.target.value)}
                        style={{
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            borderTop: 'none',
                            borderTopLeftRadius: '0',
                            borderTopRightRadius: '0'
                        }}
                        placeholder="Voer hier de HTML-code in..."
                    />
                ) : (
                    <div
                        ref={textEditorRef}
                        contentEditable="true"
                        onInput={handleTextChange}
                        onClick={handleTextEditorClick}
                        className="rich-text-editor"
                        style={{
                            minHeight: '150px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            border: '1px solid #ced4da',
                            borderTop: 'none',
                            borderBottomLeftRadius: '0.25rem',
                            borderBottomRightRadius: '0.25rem',
                            padding: '0.75rem',
                            backgroundColor: 'white',
                            outline: 'none'
                        }}
                        suppressContentEditableWarning={true}
                    />
                )}
            </Form.Group>

            {/* Image Section */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Afbeelding (optioneel)
            </h6>
            
            <Form.Group className="mb-3">
                <Form.Label>Afbeelding</Form.Label>
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowAssetSelector(true)}
                        style={{ 
                            filter: 'grayscale(100%)',
                            minWidth: '35.2px',
                            height: '35.2px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        🖼️
                    </Button>
                    <Form.Control
                        type="text"
                        value={imageUrl}
                        onChange={(e) => handleFieldChange('image_url', e.target.value)}
                        placeholder="Voer afbeelding URL in"
                        disabled
                    />
                </div>
                {imageUrl && (
                    <div className="mt-2">
                        <img 
                            src={imageUrl} 
                            alt="Selected" 
                            style={{ 
                                width: '100%', 
                                maxHeight: '200px',
                                objectFit: 'cover',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                background: backgroundOptions.find(opt => opt.value === backgroundColor)?.style || '#f5f5f5'
                            }}
                            onClick={() => window.open(imageUrl, '_blank')}
                        />
                    </div>
                )}
            </Form.Group>

            {/* Button Section */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Knop (optioneel)
            </h6>
            
            <Row className="mb-3">
                <Col md={8}>
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            type="text"
                            value={buttonText}
                            onChange={(e) => handleFieldChange('button_text', e.target.value)}
                            placeholder="Voer knop tekst in"
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Kleur</Form.Label>
                        <Form.Select
                            value={buttonColor}
                            onChange={(e) => handleFieldChange('button_color', e.target.value)}
                        >
                            <option value="default">Groen</option>
                            <option value="highlight">Oranje</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            
            <Form.Group className="mb-3">
                <Form.Label>Link</Form.Label>
                <Form.Control
                    type="text"
                    value={buttonUrl}
                    onChange={(e) => handleFieldChange('button_url', e.target.value)}
                    placeholder="Voer link in"
                />
            </Form.Group>

            {/* Asset Selector Modal */}
            <UkiesAssets
                open={showAssetSelector}
                setOpen={setShowAssetSelector}
                onAssetSelect={handleAssetSelect}
                token={apiToken}
            />
        </div>
    );
} 